import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDeck } from '../lib/deckContext';
import { type RenderKind } from '../lib/deck';
import { formatCents } from '../lib/format';
import { DeckImageGrid } from '../components/DeckImageGrid';
import { archiveDeck, createShare, exportPdf, fetchLayouts, type RenderUrls } from '../lib/api';

export function DeckPage() {
  const { deck, clearDeck, removeLayout, toggleRender, setIncludePricing } = useDeck();
  // The deck only stores which render kinds are selected, not their image URLs.
  // Fetch the catalog once and map renders by layout code so tiles can show the
  // real thumbnail and expand to full-res. Non-fatal: tiles fall back to placeholders.
  const [rendersByCode, setRendersByCode] = useState<Record<string, Record<string, RenderUrls>>>({});
  useEffect(() => {
    let cancelled = false;
    fetchLayouts()
      .then((r) => {
        if (cancelled) return;
        const map: Record<string, Record<string, RenderUrls>> = {};
        for (const l of r.layouts) map[l.code] = l.renders;
        setRendersByCode(map);
      })
      .catch(() => {/* tiles just stay as placeholders */});
    return () => {
      cancelled = true;
    };
  }, []);
  const [shareState, setShareState] = useState<
    | { status: 'idle' }
    | { status: 'creating' }
    | { status: 'ready'; url: string; expiresAt: string }
    | { status: 'error'; message: string }
  >({ status: 'idle' });
  const [pdfState, setPdfState] = useState<
    { status: 'idle' } | { status: 'exporting' } | { status: 'error'; message: string }
  >({ status: 'idle' });

  const total = deck.items.reduce(
    (sum, item) => sum + (item.includePricing ? item.totalPriceCents : 0),
    0,
  );

  // Deck-wide pricing: one toggle drives every layout's includePricing flag
  // (the export still carries a per-layout flag, just all set the same).
  const allPriced = deck.items.length > 0 && deck.items.every((i) => i.includePricing);
  const setAllPricing = (value: boolean) => {
    for (const i of deck.items) setIncludePricing(i.layoutCode, value);
  };

  // Remove a single selected image. If it's the layout's last image, drop the
  // whole layout from the deck (so its price stops counting toward the total).
  const removeImage = (layoutCode: string, kind: RenderKind) => {
    const it = deck.items.find((i) => i.layoutCode === layoutCode);
    if (!it) return;
    if (it.selectedRenders.length <= 1) removeLayout(layoutCode);
    else toggleRender(layoutCode, kind);
  };

  // Deck items in the export/share/archive wire shape.
  const buildItems = () =>
    deck.items.map((i) => ({
      layoutCode: i.layoutCode,
      familyName: i.familyName,
      familySlug: i.familySlug,
      totalPriceCents: i.totalPriceCents,
      itemCount: i.itemCount,
      widthInches: i.widthInches,
      depthInches: i.depthInches,
      includePricing: i.includePricing,
      selectedRenders: i.selectedRenders,
    }));

  // Default archive name: the deck's own name, else "<customer> — <date>", else "Deck — <date>".
  const defaultDeckName = (): string => {
    const date = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const customer = deck.customerName?.trim();
    return deck.name?.trim() || (customer ? `${customer} — ${date}` : `Deck — ${date}`);
  };

  // Archive the current deck to the rep's history, then clear the working deck.
  const archiveAndClear = async (): Promise<void> => {
    await archiveDeck({
      name: defaultDeckName(),
      customerName: deck.customerName || undefined,
      items: buildItems(),
    });
    clearDeck();
  };

  const handleExportPdf = async (): Promise<void> => {
    setPdfState({ status: 'exporting' });
    try {
      await exportPdf({
        mode: 'deck',
        items: buildItems(),
        customerName: deck.customerName || undefined,
        deckName: deck.name || undefined,
      });
    } catch (e) {
      setPdfState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
      return;
    }
    // PDF downloaded — archive to history and clear the working deck.
    try {
      await archiveAndClear();
      setPdfState({ status: 'idle' });
    } catch (e) {
      setPdfState({
        status: 'error',
        message: `Downloaded, but couldn't save to your decks: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  };

  const handleCreateShare = async (): Promise<void> => {
    setShareState({ status: 'creating' });
    let resp;
    try {
      resp = await createShare({ items: buildItems(), name: deck.name, customerName: deck.customerName });
    } catch (e) {
      setShareState({ status: 'error', message: e instanceof Error ? e.message : String(e) });
      return;
    }
    setShareState({ status: 'ready', url: resp.url, expiresAt: resp.expiresAt });
    // Best-effort archive + clear (the share link is the primary result).
    try {
      await archiveAndClear();
    } catch {
      /* keep the working deck if archiving fails */
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-vari-beige/20">
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <Link to="/" className="text-sm text-vari-blue-accessible hover:underline">
              ← Back to catalog
            </Link>
            <h1 className="text-3xl font-semibold text-vari-navy mt-2">Deck Builder</h1>
            <p className="text-vari-gray text-sm mt-1">
              {deck.items.length === 0
                ? 'No layouts yet'
                : `${deck.items.length} layout${deck.items.length === 1 ? '' : 's'} selected`}
            </p>
          </div>
          {deck.items.length > 0 && (
            <div className="text-right space-y-2">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-vari-gray">
                  Total (priced items)
                </p>
                <p className="text-3xl font-semibold text-vari-navy">{formatCents(total)}</p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allPriced}
                  onChange={(e) => setAllPricing(e.target.checked)}
                  className="accent-vari-navy h-4 w-4"
                />
                <span className="text-sm text-vari-navy">Include pricing</span>
              </label>
            </div>
          )}
        </div>

        {deck.items.length === 0 ? (
          <EmptyDeck />
        ) : (
          <>
            <DeckImageGrid
              items={deck.items}
              rendersByCode={rendersByCode}
              onRemoveImage={removeImage}
            />

            <ExportPanel
              onClear={clearDeck}
              onCreateShare={handleCreateShare}
              onExportPdf={handleExportPdf}
              itemCount={deck.items.length}
              shareInFlight={shareState.status === 'creating'}
              pdfInFlight={pdfState.status === 'exporting'}
            />
          </>
        )}
      </div>

      {shareState.status === 'ready' && (
        <ShareLinkModal
          url={shareState.url}
          expiresAt={shareState.expiresAt}
          onClose={() => setShareState({ status: 'idle' })}
        />
      )}
      {shareState.status === 'error' && (
        <ShareErrorModal
          message={shareState.message}
          onClose={() => setShareState({ status: 'idle' })}
        />
      )}
      {pdfState.status === 'error' && (
        <ShareErrorModal
          message={pdfState.message}
          onClose={() => setPdfState({ status: 'idle' })}
        />
      )}
    </div>
  );
}

function EmptyDeck() {
  return (
    <div className="rounded-2xl border border-vari-soft-gray bg-white p-10 text-center">
      <p className="text-vari-navy font-semibold text-lg">Your deck is empty</p>
      <p className="text-vari-gray text-sm mt-2 max-w-md mx-auto">
        Add layouts from the catalog to build a custom presentation for your customer. Each layout
        you add can be customized here before you export or share.
      </p>
      <Link
        to="/"
        className="inline-block mt-6 rounded-lg bg-vari-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-vari-blue-accessible transition-colors"
      >
        Browse the catalog
      </Link>
    </div>
  );
}

function ExportPanel({
  onClear,
  onCreateShare,
  onExportPdf,
  itemCount,
  shareInFlight,
  pdfInFlight,
}: {
  onClear: () => void;
  onCreateShare: () => void;
  onExportPdf: () => void;
  itemCount: number;
  shareInFlight: boolean;
  pdfInFlight: boolean;
}) {
  return (
    <div className="rounded-2xl border border-vari-soft-gray bg-white p-6 sticky bottom-4 shadow-lg shadow-black/10">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-medium text-vari-navy">Ready to share?</p>
          <p className="text-xs text-vari-gray">
            Generate a PDF or a private link for your customer. {itemCount} layout
            {itemCount === 1 ? '' : 's'} in this deck.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Clear your deck? This cannot be undone.')) onClear();
            }}
            className="text-xs text-vari-red hover:underline mr-2"
          >
            Clear deck
          </button>
          <button
            type="button"
            onClick={onCreateShare}
            disabled={shareInFlight}
            className="rounded-lg border border-vari-soft-gray text-vari-gray px-4 py-2.5 text-sm font-medium hover:border-vari-navy hover:text-vari-navy transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {shareInFlight ? 'Creating link…' : 'Create share link'}
          </button>
          <button
            type="button"
            onClick={onExportPdf}
            disabled={pdfInFlight}
            className="rounded-lg bg-vari-navy text-white px-4 py-2.5 text-sm font-semibold hover:bg-vari-blue-accessible transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pdfInFlight ? 'Generating PDF…' : 'Export PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareLinkModal({
  url: relativeUrl,
  expiresAt,
  onClose,
}: {
  url: string;
  expiresAt: string;
  onClose: () => void;
}) {
  // Permanent share link to the persisted deck PDF (served at /d/<id>).
  const url = `${window.location.origin}${relativeUrl}`;
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard not available — fall back to manual select
    }
  };

  const expires = new Date(expiresAt);
  const daysLeft = Math.round((expires.getTime() - Date.now()) / (24 * 60 * 60 * 1000));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-vari-navy/40 px-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-vari-blue-accessible">
            Share link ready
          </p>
          <h2 className="text-xl font-semibold text-vari-navy mt-1">Send to your customer</h2>
          <p className="text-vari-gray text-sm mt-1">
            Anyone with this link can view the deck. Expires in {daysLeft} day
            {daysLeft === 1 ? '' : 's'}.
          </p>
        </div>

        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 rounded-lg border border-vari-soft-gray bg-vari-beige/30 px-3 py-2.5 text-sm font-mono text-vari-navy focus:outline-none focus:border-vari-blue-accessible"
          />
          <button
            type="button"
            onClick={copy}
            className={`rounded-lg px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${
              copied
                ? 'bg-vari-green text-vari-navy'
                : 'bg-vari-navy text-white hover:bg-vari-blue-accessible'
            }`}
          >
            {copied ? '✓ Copied' : 'Copy link'}
          </button>
        </div>

        <div className="flex items-center justify-between pt-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-vari-blue-accessible hover:underline"
          >
            Preview as customer →
          </a>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-vari-gray hover:text-vari-navy transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareErrorModal({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-vari-navy/40 px-6"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-vari-navy">Couldn't create link</h2>
        <p className="text-sm text-vari-gray">{message}</p>
        <div className="text-right">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-vari-navy text-white px-4 py-2 text-sm font-semibold hover:bg-vari-blue-accessible transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
